import { useRouteLoaderData, useSearchParams } from 'react-router';
import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import ProjectsPage, {
    handle as projectsHandle,
} from '@modules/professional/page/ProjectsPage';
import { loader as projectsLoader } from '@modules/professional/page/loaders/projects-page-loader.server';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import { isProfessionalRevampActive } from '@modules/professional/util/is-professional-revamp-active.server';
import { getSession } from '~/modules/root/util/session.server';
import { ProfessionalProjectsPageRevamp } from '~/ui-rocco/pages/professional-detail/ProfessionalProjectsPageRevamp';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import type { APHandle } from '~/modules/root';

/**
 * When the revamp is active the Projects tab becomes a full editorial page: the
 * reused hero/contact/closing need the overview data, while the dark grid needs
 * the projects page-1 data (tiles + pagination + filters), so the loader merges
 * both. Otherwise it serves the legacy paginated projects grid.
 */
export const loader = async (args: LoaderFunctionArgs) => {
    if (!isProfessionalRevampActive(args.context, args.request)) {
        return projectsLoader(args);
    }

    // The revamp's shared overview payload comes from the persistent layout
    // loader; this route only fetches the projects tab's own data.
    const projectsData = await projectsLoader(args);
    const session = await getSession(args.request);

    return routerData(
        {
            projectTilesRocco: projectsData.projectTilesRocco,
            pagination: projectsData.pagination,
            filterStates: projectsData.filterStates,
            filterStatesRocco: projectsData.filterStatesRocco,
            defaultViewProjectsBy: projectsData.defaultViewProjectsBy,
            aggregateScopeFilters: projectsData.aggregateScopeFilters,
            searchResult: projectsData.searchResult,
        },
        { headers: await session.getHeaders() },
    );
};

export const handle: APHandle = {
    ...projectsHandle,
    professionalRevampLayout: true,
};

const ProfessionalProjectsRoute = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const [searchParams] = useSearchParams();
    const showRevamp =
        layoutData?.detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    return showRevamp ? <ProfessionalProjectsPageRevamp /> : <ProjectsPage />;
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ProfessionalProjectsRoute;
